import { useCallback, useEffect, useMemo, useState } from "react";
import { getInitialSearchOptions, INIT_PRICE_RANGE } from "@/data/constants/HelloretailSearch";
import { useSettings } from "@/data/Settings";
import { useNextRouter } from "@/data/Content/_NextRouter";
import { getProductListQueryParameters } from "@/data/utils/getProductListQueryParameters";
import { EMPTY_STRING } from "@/data/constants/marketing";
import { HelloretailProductType, CategoryFacetItem, HelloRetailPriceRange, HelloRetailRedirect, HelloRetailFiltersMap } from "@/data/types/HelloRetail";
import { ContentProps } from "@/data/types/ContentProps";
import { getLocalization } from "@/data/Localization-Server";
import { useMediaQuery, useTheme } from "@mui/material";
import { dataMap as helloRetailProductDataMap, getFiltersShowMore, sortFilterOptionsBySectionState } from "@/data/utils/helloRetailSearch";

export const getHelloRetailSearch = async ({ cache, context }: ContentProps) => {
	await Promise.all([
		getLocalization(cache, context.locale || 'nl-NL', 'ProductFilter'),
	]);
}

export const useHelloRetailSearch = () => {

    const { settings } = useSettings();
    const router = useNextRouter();
    const { query } = router;
    const helloretailSearchConfigKey = useMemo(()=>settings.userData.HelloretailSearchConfigKey,[settings]);

    const filteredParams = useMemo(() => getProductListQueryParameters(query), [query]);

    const { searchTerm } = filteredParams;

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const INITIAL_SEARCH_OPTION = useMemo(()=>(getInitialSearchOptions({
		categoryCount : parseInt(settings.userData.HelloretailCategoryLinkCount??'25'),
		productCount : parseInt(settings.userData.HelloretailProductCount??'36')
	})),[settings]);

    const [showOverLay, setShowOverLay] = useState(false);
    const [searchValue, setSearchValue] = useState(searchTerm??EMPTY_STRING);
    const [filtersShowMore, setFiltersShowMore] = useState<Record<string, boolean>>({});

    const [sortyBy, setSortBy] = useState("relevance");
    const [pageNumber, setPageNumber] = useState(1);
	const [priceRange, setPriceRange] = useState<HelloRetailPriceRange>(INIT_PRICE_RANGE);
	const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
	const [searchOptions, setSearchOptions] = useState(INITIAL_SEARCH_OPTION);

	const [products, setProducts] = useState<HelloretailProductType[]>([]);
	const [filters, setFilters] = useState<HelloRetailFiltersMap>({});
	const [filterKeys, setFilterKeys] = useState<string[]>([]);
	const [filterTitles, setFilterTitles] = useState<Record<string, string>>({});
	const [resultCount, setResultCount ] = useState(0);
	const [categoryFacet, setCategoryFacet] = useState<CategoryFacetItem[]>([]); // This state is not used in the provided code, consider removing it if not needed.
	const [redirect, setRedirect] = useState<HelloRetailRedirect[]>([]);
	const [loading, setLoading] = useState(false);

	const pushToHelloRetailQuery = useCallback(()=>{
		if (typeof window !== "undefined" && window.hrq?.push && searchValue?.length) {
			setLoading(true);
			window.hrq.push([
				"search",
				helloretailSearchConfigKey,
				searchValue,
				searchOptions,
				(searchData: any) => {
					const mappedProducts: HelloretailProductType[] = helloRetailProductDataMap(searchData?.result ?? []);
					setProducts(mappedProducts);
					setFilters(searchData?.filters ? sortFilterOptionsBySectionState(searchData?.filters) : {});
					const tempFilterKeys = Object.keys(searchData?.filter_titles ?? {});
					setFilterKeys(tempFilterKeys);
					setFilterTitles(searchData?.filter_titles ?? {});
					setResultCount(searchData.product_results);
					setFiltersShowMore(getFiltersShowMore(searchData?.filters ?? [],tempFilterKeys));
					setCategoryFacet(searchData?.categories ?? []);
					setRedirect(searchData?.redirects??[]);
					setLoading(false);
				}
			]);
		} else {
			setProducts([]);
			setCategoryFacet([]);
			setSearchOptions(INITIAL_SEARCH_OPTION)
		}
	},[
		helloretailSearchConfigKey,
		searchValue,
		searchOptions
	]);


	useEffect(() => {
		const debounceTimer = setTimeout(() => {
			pushToHelloRetailQuery();
		},300);

		return () => clearTimeout(debounceTimer);
	}, [searchValue, searchOptions]);

	useEffect(()=>{
		window.addEventListener('CookiebotOnLoad', function (e) {
			if (typeof window !== "undefined" && window.hrq?.push) {
				if (window.Cookiebot?.consent?.marketing) {
					window.hrq.push([
						'init',
						{
							websiteUuid: settings.userData.HelloRetailUUID,
							trackingOptOut: false
						}
					]);
				} else {
					window.hrq.push([
						'init',
						{
							websiteUuid: settings.userData.HelloRetailUUID,
							trackingOptOut: true
						}
					]);
				}
				window.__hasHelloretailInitialized = true;
			}
			 
		}, false);
		
	},[]);

	useEffect(()=>{
		const tempOptions = {...searchOptions};
		tempOptions.filters = selectedFilters;
		tempOptions.engine_options.product.start = 0;
		setPageNumber(1);
		setSearchOptions(tempOptions);
	},[selectedFilters]);

	useEffect(()=>{
		const tempOptions = {...searchOptions};
		tempOptions.sorting = [sortyBy === "relevance" ? "" : sortyBy];
		setSearchOptions(tempOptions);
	},[sortyBy]);

	useEffect(()=>{
		const tempOptions = {...searchOptions};
		tempOptions.engine_options.product.start = ((pageNumber-1)*36);
		setSearchOptions(tempOptions);
	},[pageNumber]);

	useEffect(()=>{
		setSearchOptions(INITIAL_SEARCH_OPTION);
		setSortBy("relevance");
		setPageNumber(1);
		setPriceRange(INIT_PRICE_RANGE);
		setSelectedFilters([]);
	},[searchValue]);

    useEffect(()=>{
        setShowOverLay(false);
    },[router.asPath]);

    useEffect(()=>{
        if(searchTerm)setSearchValue(searchTerm);
    },[searchTerm]);

	useEffect(()=>{
		const body = document.querySelector<HTMLBodyElement>("body");
		if (body) body.style.overflow = showOverLay && !isMobile ? 'hidden' : 'auto';
		return ()=>{
			if (body) body.style.overflow = 'auto';
		}
	},[showOverLay, !isMobile]);

    const handleFilterChange = (checked: boolean, filter:string) => {
		let tempSFilters = [...selectedFilters];
		if(checked){
			tempSFilters.push(filter);
		} else {
			tempSFilters = tempSFilters?.filter(t=>t!==filter);
		} 
		setSelectedFilters(tempSFilters);
	}

	const handlePriceFilterChange = (priceRange:HelloRetailPriceRange) => {
		let tempSFilters = [...selectedFilters];
		if(priceRange.minPrice > -1 && priceRange.maxPrice > -1){
			tempSFilters.push(`price:${priceRange.minPrice},${priceRange.maxPrice}`);
		} else {
			tempSFilters = tempSFilters?.filter(t=>!t.includes("price:"));
		} 
		setSelectedFilters(tempSFilters);
		setPriceRange(priceRange);
	}
	
	const toggleFacetShowMore = (filterKey:string) => {
		const tempFiltersShowMore = {...filtersShowMore};
		tempFiltersShowMore[filterKey] = !tempFiltersShowMore[filterKey];
		setFiltersShowMore(tempFiltersShowMore);
	}

	const helloRetailClickTracking = (trackingCode:string) => {
		if (typeof window !== "undefined" && window.hrq?.push){
			const hrq = window.hrq || [];
			hrq.push([
				"trackClick",
				trackingCode
			]);
		}
	}

	const closeOverLay = () => {
		setShowOverLay(false);
		setSearchValue("");
	}

	return {
        searchValue,
        setSearchValue,
        loading,
        setLoading,
        products,
        filters,
        filterKeys,
        filterTitles,
        resultCount,
        sortyBy,
        setSortBy,
        pageNumber,
        setPageNumber,
        filtersShowMore,
        toggleFacetShowMore,
        handleFilterChange,
        helloRetailClickTracking,
        filteredParams,
        showOverLay,
        setShowOverLay,
		closeOverLay,
		categoryFacet,
		priceRange,
		handlePriceFilterChange,
		redirect
    }
}